import { z } from 'zod';
/**
 * 매핑 항목. external = 외부 시스템의 키 (DB 컬럼명 또는 API 응답 row 의 키 / array index '[0]'),
 * internal = innermate 의 내부 필드. 예약된 internal 키:
 *   - 'email'        — users.email (필수, identityKey 일 수도 있음)
 *   - 'displayName'  — users.display_name (선택)
 *   - 'disabled'     — boolean. true 면 즉시 비활성화 (재고용 등 명시적 마킹).
 *   - 그 외          — user_attribute_definitions.key 로 간주.
 */
export const SyncFieldMappingSchema = z.object({
    external: z.string().min(1).max(120),
    internal: z.string().min(1).max(60),
});
export const SyncKindSchema = z.enum(['db', 'http']);
const baseSync = {
    /** users.email 또는 attribute key. upsert 식별자. fieldMappings 에 internal=identityKey 가 반드시 있어야 함. */
    identityKey: z.string().min(1).max(60),
    fieldMappings: z.array(SyncFieldMappingSchema).min(1).max(50),
    /** graphile-worker crontab. null/빈 문자열 = 매뉴얼 only. */
    cronExpression: z.string().max(80).nullable().optional(),
    enabled: z.boolean(),
};
export const UserSyncConfigSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('db'),
        dataSourceId: z.string().uuid(),
        schemaName: z.string().min(1).max(120),
        tableName: z.string().min(1).max(120),
        ...baseSync,
    }),
    z.object({
        kind: z.literal('http'),
        httpEndpointId: z.string().uuid(),
        /** 엔드포인트 baseUrl 뒤에 붙는 path. ex: '/api/v1/employees'. */
        requestPath: z.string().min(1).max(500),
        requestMethod: z.enum(['GET', 'POST']),
        /** POST body (JSON 문자열). 빈 문자열이면 body 없음. */
        requestBody: z.string().max(10000).optional(),
        /** querystring (예: 'page=1&size=1000'). */
        requestQuery: z.string().max(1000).optional(),
        /** 응답에서 list 까지의 도트 경로. 예: 'result.data'. 빈 문자열 = root 자체가 list. */
        listJsonPath: z.string().max(200).optional(),
        ...baseSync,
    }),
]);
/** GET /api/admin/user-sync 응답. 미설정이면 config null. */
export const UserSyncConfigResponseSchema = z.object({
    config: UserSyncConfigSchema.nullable(),
});
/** preview 결과 — UI 가 매핑 빌더에서 외부 키 선택지로 사용. */
export const UserSyncPreviewResponseSchema = z.object({
    /** 외부 키 후보 (객체 row 면 키 이름, 배열 row 면 '[0]', '[1]' …). */
    externalKeys: z.array(z.string()),
    /** 샘플 row (최대 5개). UI 에 미리보기. */
    sampleRows: z.array(z.record(z.unknown())),
    /** kind='http' 일 때 응답 트리에서 BFS 로 감지된 list 후보 path (자동완성용). */
    listCandidates: z.array(z.object({
        path: z.string(),
        length: z.number().int().nonnegative(),
        /** 첫 row 가 object 면 'object', array 면 'array', 그 외 'primitive'. */
        rowKind: z.enum(['object', 'array', 'primitive']),
    })),
});
/** preview 요청. 설정 저장 전에 미리 호출. */
export const UserSyncPreviewRequestSchema = UserSyncConfigSchema;
/** runs 응답. */
export const UserSyncRunSchema = z.object({
    id: z.string().uuid(),
    trigger: z.enum(['manual', 'cron']),
    status: z.enum(['running', 'completed', 'failed']),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    totalRecords: z.number().int(),
    upsertedCount: z.number().int(),
    deactivatedCount: z.number().int(),
    errorCount: z.number().int(),
    errorMessage: z.string().nullable(),
});
export const UserSyncRunListSchema = z.object({
    runs: z.array(UserSyncRunSchema),
});
export const RunUserSyncResponseSchema = z.object({
    runId: z.string().uuid(),
});
//# sourceMappingURL=user-sync.js.map